import { createContext, useContext, ReactNode, useState, useEffect, useRef } from 'react'
import { collection, query, onSnapshot, orderBy, doc, updateDoc, limit, Timestamp } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/store/authStore'
import { Notification } from '@/types/notification'

interface NotificationContextType {
    notifications: Notification[]
    unreadCount: number
    markAsRead: (notificationId: string) => Promise<void>
    markAllAsRead: () => Promise<void>
    loading: boolean
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export function NotificationProvider({ children }: { children: ReactNode }) {
    const { user, userRole } = useAuthStore()
    const [personalNotifications, setPersonalNotifications] = useState<Notification[]>([])
    const [broadcastNotifications, setBroadcastNotifications] = useState<Notification[]>([])
    const [notifications, setNotifications] = useState<Notification[]>([])
    const [loading, setLoading] = useState(true)
    const [hiddenBroadcasts, setHiddenBroadcasts] = useState<string[]>([])
    const previousUnreadCountRef = useRef(0)

    useEffect(() => {
        if (!user) {
            setPersonalNotifications([])
            setBroadcastNotifications([])
            setHiddenBroadcasts([])
            setLoading(false)
            return
        }

        let unsubPersonal: () => void = () => { }
        let unsubBroadcast: () => void = () => { }
        let unsubHidden: () => void = () => { }

        try {
            const qPersonal = query(
                collection(db, 'users', user.uid, 'notifications'),
                orderBy('createdAt', 'desc'),
                limit(50)
            )
            unsubPersonal = onSnapshot(qPersonal, (snapshot) => {
                setPersonalNotifications(snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as Notification)))
            })
        } catch (error) {
            console.error(error)
        }

        try {
            const qBroadcast = query(
                collection(db, 'notifications_broadcast'),
                orderBy('createdAt', 'desc'),
                limit(20)
            )
            unsubBroadcast = onSnapshot(qBroadcast, (snapshot) => {
                const allBroadcasts = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as Notification & { targetGroup?: string, targetUserId?: string, targetUserIds?: string[] }))

                const filtered = allBroadcasts.filter(b => {
                    const data = b as any
                    if (b.targetGroup === 'all') return true
                    if (b.targetGroup === 'admins') {
                        return userRole === 'admin' || userRole === 'owner'
                    }
                    if (b.targetGroup === 'members') {
                        if (data.targetUserIds) {
                            return data.targetUserIds.includes(user.uid)
                        }
                        return data.targetUserId === user.uid
                    }
                    return false
                })
                setBroadcastNotifications(filtered)
            })
        } catch (error) {
            console.error(error)
        }

        try {
            const qHidden = collection(db, 'users', user.uid, 'hidden_broadcasts')
            unsubHidden = onSnapshot(qHidden, (snap) => {
                setHiddenBroadcasts(snap.docs.map(d => d.id))
            })
        } catch (e) {
            console.error("Error hidden broadcasts", e)
        }

        return () => {
            unsubPersonal()
            unsubBroadcast()
            unsubHidden()
        }
    }, [user, userRole])

    useEffect(() => {
        // Merge and Sort
        const visibleBroadcasts = broadcastNotifications.filter(b => !hiddenBroadcasts.includes(b.id))

        const combined = [...personalNotifications, ...visibleBroadcasts].sort((a, b) => {
            const dateA = a.createdAt instanceof Timestamp ? a.createdAt.toDate() : new Date(a.createdAt || 0)
            const dateB = b.createdAt instanceof Timestamp ? b.createdAt.toDate() : new Date(b.createdAt || 0)
            return dateB.getTime() - dateA.getTime()
        })

        const currentUnreadCount = combined.filter(n => !n.read).length

        if (!loading && currentUnreadCount > previousUnreadCountRef.current) {
            try {
                const audio = new Audio(`${import.meta.env.BASE_URL}sounds/notification.mp3`)
                audio.play().catch(e => console.log('Audio autoplay blocked', e))
            } catch (error) {
                console.error(error)
            }
        }

        previousUnreadCountRef.current = currentUnreadCount
        setNotifications(combined)
        setLoading(false)
    }, [personalNotifications, broadcastNotifications, hiddenBroadcasts]) // Removed loading from dependency to avoid loop, managed inside logic? 
    // Actually, loading state is set to false AT THE END of this effect.
    // So 'loading' will be true initially.
    // Initial load: loading=true. Effect runs. !loading is false. No sound. setLoading(false).
    // Second run (update): loading=false. !loading is true. Sound plays if count increases.
    // Correct.

    const unreadCount = notifications.filter(n => !n.read).length

    const markAsRead = async (notificationId: string) => {
        try {
            // Cannot mark broadcast as read individually easily without storing state in user profile.
            // For MVP, we only allow marking PERSONAL notifications as read in DB.
            // Client-side read state for broadcasts would reset on refresh.
            // We check which collection it belongs to by ID? No easy way.
            // Simpler: Try update, if fails just ignore (or check if it exists in personal list)
            const isPersonal = personalNotifications.find(n => n.id === notificationId)

            if (isPersonal) {
                await updateDoc(doc(db, 'users', user?.uid || 'unknown', 'notifications', notificationId), {
                    read: true
                })
            } else {
                // It's a broadcast, we can't write to the broadcast doc 'read' property as it is shared.
                // We would need a 'readBroadcasts' subcollection in user profile.
                // SKIPPING broadcast read state persistence for MVP.
                // Just update local state temporarily.
                setBroadcastNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n))
            }
        } catch (error) {
            console.error('Error marking notification as read:', error)
        }
    }

    const markAllAsRead = async () => {
        try {
            const unreadPersonal = personalNotifications.filter(n => !n.read)
            await Promise.all(
                unreadPersonal.map(n =>
                    updateDoc(doc(db, 'users', user?.uid || 'unknown', 'notifications', n.id), { read: true })
                )
            )
            // For broadcasts, just locally mark read
            setBroadcastNotifications(prev => prev.map(n => ({ ...n, read: true })))
        } catch (error) {
            console.error('Error marking all notifications as read:', error)
        }
    }

    return (
        <NotificationContext.Provider value={{
            notifications,
            unreadCount,
            markAsRead,
            markAllAsRead,
            loading
        }}>
            {children}
        </NotificationContext.Provider>
    )
}

export function useNotifications() {
    const context = useContext(NotificationContext)
    if (context === undefined) {
        throw new Error('useNotifications must be used within a NotificationProvider')
    }
    return context
}
